/**
 * Utility functions for consistent branding and naming throughout the application
 */

/**
 * Format the dwelling unit name (Villa/Flat/Apartment) based on context
 * @param {Object} settings - Settings from the SettingsContext
 * @param {string} [unitNumber] - Optional unit number to append
 * @param {boolean} [plural=false] - Whether to use plural form
 * @param {boolean} [capitalize=true] - Whether to capitalize the first letter
 * @param {boolean} [includeNumber=false] - Whether to include the unit number
 * @returns {string} Formatted dwelling unit text
 */
export const formatDwellingUnit = (settings, unitNumber = null, plural = false) => {
  if (!settings) return unitNumber ? `Unit ${unitNumber}` : plural ? 'Units' : 'Unit';
  
  const unitName = settings.dwelling_unit_name || 'Unit';
  // const unitType = settings.dwelling_unit_type || 'Unit';
  const prefix = settings.dwelling_unit_prefix || 'U-';
  
  if (unitNumber) {
    return `${unitName} ${prefix}${unitNumber}`;
  }
  
  if (plural) {
    // Handle English pluralization rules (not perfect for all cases but covers common ones)
    if (unitName.endsWith('y')) {
      return `${unitName.slice(0, -1)}ies`;
    } else if (unitName.endsWith('s') || unitName.endsWith('x') || unitName.endsWith('z')) {
      return `${unitName}es`;
    } else {
      return `${unitName}s`;
    }
  }
  
  return unitName;
};

/**
 * Format currency according to settings
 * @param {Object} settings - Settings from the SettingsContext
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency
 */
export const formatCurrency = (settings, amount) => {
  if (!settings) return `₹${amount.toFixed(2)}`;
  
  const currencySymbol = settings.currencySymbol || '₹';
  const currency = settings.currency || 'INR';
  
  try {
    return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  } catch (error) {
    console.error('Currency formatting error:', error);
    return `${currencySymbol}${amount.toFixed(2)}`;
  }
};

/**
 * Format dates according to settings
 * @param {Object} settings - Settings from the SettingsContext
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date
 */
export const formatDate = (settings, date) => {
  if (!date) return '';
  if (!settings) return new Date(date).toLocaleDateString();
  
  const dateFormat = settings.dateFormat || 'MM/DD/YYYY';
  const dateObj = new Date(date);
  
  if (dateFormat === 'DD/MM/YYYY') {
    return dateObj.toLocaleDateString('en-GB');
  } else if (dateFormat === 'YYYY-MM-DD') {
    return dateObj.toISOString().split('T')[0];
  } else {
    return dateObj.toLocaleDateString('en-US');
  }
};

/**
 * Get application name from settings
 * @param {Object} settings - Settings from the SettingsContext
 * @returns {string} Application name
 */
export const getAppName = (settings) => {
  if (!settings) return 'Society Management App';
  return settings.system_name || 'Society Management App';
};

/**
 * Get society name from settings
 * @param {Object} settings - Settings from the SettingsContext
 * @returns {string} Society name
 */
export const getSocietyName = (settings) => {
  // Try to get from session storage first
  try {
    const userStr = sessionStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      if (user.organization_name) {
        return user.organization_name;
      }
    }
  } catch (err) {
    console.log('Error reading organization name from session:', err);
  }
  
  // Then try settings
  if (settings && settings.society_name) {
    return settings.society_name;
  }
  
  if (settings && settings.organization_name) {
    return settings.organization_name;
  }
  
  // Finally try localStorage as last resort
  try {
    const localOrgName = localStorage.getItem('organization_name');
    if (localOrgName) {
      return localOrgName;
    }
  } catch (err) {
    console.log('Error reading organization name from localStorage:', err);
  }
  
  return 'Organization Name';
}; 